import { AssetManagementRolesEnum } from "../../utils/constants";
import { getCookie } from "../../utils/cookies";

/** Columns hidden for investor (INVESTOR_MEMBER) list view. */
export const INVESTOR_MEMBER_HIDDEN_COLUMN_IDS = new Set([
  "documentReviewStatus",
  "documentReviewDate",
  "updatedByName",
  "purchasedDate"
]);

export type PurchasedLoansAccess = {
  /** User has INVESTOR_MEMBER (scoped investor experience). */
  isInvestorMember: boolean;
  /** User has PURCHASED_LOANS_VIEWER (read-only mutation locks). */
  isReadOnly: boolean;
  /** Whether document/upload mutations should be allowed. */
  canMutateDocuments: boolean;
  /** Whether AM Post-Purchase notes can be created/edited/deleted. */
  canMutateAmNotes: boolean;
  /** Investor users get a purchased-loans-only shell (tabs/nav). */
  usesInvestorOnlyNavigation: boolean;
  /** Default purchased-loans list header tab id. */
  defaultListTabId: number;
  /** List columns that should be hidden for this access profile. */
  hiddenListColumnIds: ReadonlySet<string>;
};

const parseRolesCookie = (): string[] => {
  let roleArr: unknown = getCookie("roles");
  if (roleArr === null || roleArr === undefined) return [];
  try {
    roleArr = typeof roleArr === "string" ? JSON.parse(roleArr) : roleArr;
  } catch {
    return [];
  }
  return Array.isArray(roleArr) ? roleArr.map(String) : [];
};

/**
 * Derives purchased-loans capabilities from role names.
 * Prefer this over scattering role-string checks in UI code.
 */
export function getPurchasedLoansAccess(
  roles: string[] | null | undefined
): PurchasedLoansAccess {
  const roleSet = new Set(roles || []);
  const isInvestorMember = roleSet.has(
    AssetManagementRolesEnum.INVESTOR_MEMBER
  );
  const isReadOnly = roleSet.has(
    AssetManagementRolesEnum.PURCHASED_LOANS_VIEWER
  );

  return {
    isInvestorMember,
    isReadOnly,
    canMutateDocuments: !isReadOnly,
    canMutateAmNotes: !isInvestorMember && !isReadOnly,
    usesInvestorOnlyNavigation: isInvestorMember,
    defaultListTabId: isInvestorMember ? 14 : 0,
    hiddenListColumnIds: isInvestorMember
      ? INVESTOR_MEMBER_HIDDEN_COLUMN_IDS
      : new Set()
  };
}

/** Convenience wrapper that reads roles from the auth cookie. */
export function getCurrentPurchasedLoansAccess(): PurchasedLoansAccess {
  return getPurchasedLoansAccess(parseRolesCookie());
}
